import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trees, RefreshCw, Play, Search, BarChart3 } from "lucide-react";
import { authFetch } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import ModuleHeader from "@/components/ModuleHeader";

function fmtNum(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR");
}
function fmtDate(d: string | null | undefined) {
  if (!d) return "Nunca";
  return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

const UFS = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];
const GRUPOS = [
  { key: "PROTECAO_INTEGRAL", label: "Proteção Integral" },
  { key: "USO_SUSTENTAVEL", label: "Uso Sustentável" },
];
function grupoLabel(g: string) {
  if (g === "PROTECAO_INTEGRAL") return "Proteção Integral";
  if (g === "USO_SUSTENTAVEL") return "Uso Sustentável";
  return g;
}

export default function IcmbioPage() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [filterGrupo, setFilterGrupo] = useState("TODOS");
  const [filterUf, setFilterUf] = useState("TODOS");
  const [filterBusca, setFilterBusca] = useState("");
  const [buscaInput, setBuscaInput] = useState("");
  const [ucPage, setUcPage] = useState(0);
  const UC_LIMIT = 50;

  const statusQ = useQuery({
    queryKey: ["/api/icmbio/status"],
    queryFn: () => authFetch("/api/icmbio/status"),
    refetchInterval: 5000,
  });

  const ucsParams = new URLSearchParams();
  if (filterGrupo !== "TODOS") ucsParams.set("grupo", filterGrupo);
  if (filterUf !== "TODOS") ucsParams.set("uf", filterUf);
  if (filterBusca) ucsParams.set("busca", filterBusca);
  ucsParams.set("limit", String(UC_LIMIT));
  ucsParams.set("offset", String(ucPage * UC_LIMIT));

  const ucsQ = useQuery({
    queryKey: ["/api/icmbio/ucs", filterGrupo, filterUf, filterBusca, ucPage],
    queryFn: () => authFetch(`/api/icmbio/ucs?${ucsParams}`),
  });

  const importMut = useMutation({
    mutationFn: () => authFetch("/api/icmbio/importar", { method: "POST" }),
    onSuccess: (d: any) => {
      toast({ title: "Importação iniciada", description: d.message });
      qc.invalidateQueries({ queryKey: ["/api/icmbio/status"] });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const status = statusQ.data as any;
  const ucs = ucsQ.data as any;
  const porGrupo = status?.por_grupo || {};
  const syncAtivo = status?.sync_ativo;
  const progressPct = status?.sync_total > 0
    ? Math.round((status.sync_progresso / status.sync_total) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <ModuleHeader
        icon={Trees}
        iconColor="text-green-700"
        title="ICMBio — Unidades de Conservação"
        description="UCs importadas do GeoServer ICMBio (CNUC). Sync mensal automático."
        recordCount={(statusQ.data as any)?.total}
        recordLabel="UCs"
        onRefresh={() => qc.invalidateQueries({ queryKey: ["/api/icmbio/status"] })}
        refreshing={statusQ.isFetching}
        isLoading={statusQ.isLoading}
        docsHref="/docs"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Trees className="h-4 w-4 text-green-700" />
              <span className="text-xs text-muted-foreground">Total de UCs</span>
            </div>
            <div className="text-2xl font-bold text-green-700">
              {statusQ.isLoading ? <Skeleton className="h-7 w-16" /> : fmtNum(status?.total)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">Última sync: {fmtDate(status?.ultima_sync)}</div>
          </CardContent>
        </Card>

        {GRUPOS.map((g) => (
          <Card key={g.key}>
            <CardContent className="pt-4 pb-3">
              <div className="text-xs text-muted-foreground mb-1">{g.label}</div>
              <div className="text-2xl font-bold">
                {statusQ.isLoading ? <Skeleton className="h-7 w-12" /> : fmtNum(porGrupo[g.key] ?? 0)}
              </div>
            </CardContent>
          </Card>
        ))}

        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Status sync</span>
            </div>
            <div className="text-sm font-medium">
              {syncAtivo ? (
                <Badge variant="secondary" className="animate-pulse">Sincronizando…</Badge>
              ) : status?.erro ? (
                <Badge variant="destructive">Erro</Badge>
              ) : (
                <Badge variant="outline">Ocioso</Badge>
              )}
            </div>
            {status?.duracao_seg > 0 && (
              <div className="text-xs text-muted-foreground mt-0.5">
                Última duração: {status.duracao_seg}s
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {syncAtivo && (
        <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">
          <CardContent className="pt-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium">Sincronizando UCs…</span>
              <span>{fmtNum(status.sync_progresso)} / {fmtNum(status.sync_total)}</span>
            </div>
            <Progress value={progressPct} className="h-2" />
            {status?.sync_log && <p className="text-xs text-muted-foreground mt-2">{status.sync_log}</p>}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Importação Manual</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Importa todas as UCs via WFS do GeoServer ICMBio (geoservicos.inde.gov.br). Cron automático: dia 1 de cada mês, às 02:00.
          </p>
          {status?.erro && (
            <p className="text-sm text-destructive">Último erro: {status.erro}</p>
          )}
          <Button
            onClick={() => importMut.mutate()}
            disabled={importMut.isPending || syncAtivo}
            data-testid="button-importar-icmbio"
          >
            <Play className="h-4 w-4 mr-1" />
            {syncAtivo ? "Sincronizando…" : "Sincronizar UCs Agora"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" /> Consultar Unidades de Conservação
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="w-48">
              <Label className="text-xs mb-1">Grupo</Label>
              <Select value={filterGrupo} onValueChange={(v) => { setFilterGrupo(v); setUcPage(0); }}>
                <SelectTrigger data-testid="select-grupo-icmbio">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TODOS">Todos os grupos</SelectItem>
                  {GRUPOS.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-32">
              <Label className="text-xs mb-1">UF</Label>
              <Select value={filterUf} onValueChange={(v) => { setFilterUf(v); setUcPage(0); }}>
                <SelectTrigger data-testid="select-uf-icmbio">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TODOS">Todas</SelectItem>
                  {UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-48">
              <Label className="text-xs mb-1">Busca por nome</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Nome da UC..."
                  value={buscaInput}
                  onChange={(e) => setBuscaInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { setFilterBusca(buscaInput); setUcPage(0); } }}
                  data-testid="input-busca-icmbio"
                />
                <Button size="sm" onClick={() => { setFilterBusca(buscaInput); setUcPage(0); }} data-testid="button-buscar-icmbio">
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {ucsQ.isLoading ? (
            <div className="space-y-2">{Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">{fmtNum((ucs as any)?.total)} UCs encontradas</p>
              <div className="overflow-x-auto rounded border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead>Esfera</TableHead>
                      <TableHead>UF</TableHead>
                      <TableHead className="text-right">Área (ha)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(ucs?.data || []).map((r: any, i: number) => (
                      <TableRow key={i} data-testid={`row-uc-${i}`}>
                        <TableCell className="font-medium text-sm">{r.nome || "—"}</TableCell>
                        <TableCell className="text-xs">{r.categoria || "—"}</TableCell>
                        <TableCell>
                          <Badge variant={r.grupo === "PROTECAO_INTEGRAL" ? "destructive" : "secondary"} className="text-xs">
                            {grupoLabel(r.grupo) || "—"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{r.esfera || "—"}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{r.uf || "—"}</Badge></TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {r.area_ha ? Number(r.area_ha).toLocaleString("pt-BR", { maximumFractionDigits: 0 }) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={ucPage === 0}
                  onClick={() => setUcPage((p) => p - 1)}
                  data-testid="button-prev-page-icmbio"
                >
                  Anterior
                </Button>
                <span className="text-xs text-muted-foreground">Página {ucPage + 1}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(ucs?.data?.length || 0) < UC_LIMIT}
                  onClick={() => setUcPage((p) => p + 1)}
                  data-testid="button-next-page-icmbio"
                >
                  Próxima
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
